import { TellescopeModelFromName, useAuth, useUser } from "@tellescope/tanstack-query";
import { useCurrentTimestamp } from "@tellescope/ui";
import { addDays } from "date-fns/addDays";
import { useMemo } from "react";
import { AvailabilityTimeRange, getAvailableRangesForDay } from "../utils";
import { CalendarFilters } from "../filters";
import { useCalendarEventsWithFilters } from "./use-calendar-events-with-filters";
import { isFuture } from "date-fns/isFuture";

export interface UseAvailabilityRangesArgs {
  userId: string;
  timezone?: string;
  filters?: CalendarFilters;
  weekStart?: "sunday" | "monday";
  /** Number of days beyond today to include. Defaults to 0 (current day only). */
  futureDayCount?: number;
  enabled?: boolean;
  events?: TellescopeModelFromName<"calendar_events">[];
  referenceDate?: Date;
}

export const useAvailabilityRanges = ({
  userId,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  filters,
  weekStart,
  futureDayCount = 0,
  enabled: providedEnabled = true,
  events: providedEvents,
  referenceDate,
}: UseAvailabilityRangesArgs): AvailabilityTimeRange[] => {
  const user = useUser(userId);
  const { currentUser } = useAuth();
  const enabled = providedEnabled && !!user?.weeklyAvailabilities?.length;
  const now = useCurrentTimestamp({
    pollingRateInMs: enabled && !referenceDate ? 1000 * 60 : Infinity,
    snapTo: "minute",
  });
  const calcDate = referenceDate ?? now;

  const dayCount = Math.max(0, Math.floor(futureDayCount));

  const eventsQuery = useCalendarEventsWithFilters({
    referenceDate: calcDate,
    view: dayCount === 0 ? "day" : "month",
    timezone: user?.timezone ?? timezone,
    weekStart,
    filters,
    currentUserId: currentUser.id,
    enabled: !providedEvents,
  });

  return useMemo(() => {
    const availabilities = user?.weeklyAvailabilities ?? [];
    if (availabilities.length === 0 || !enabled) return [];

    const calcMs = calcDate.getTime();
    const events = (providedEvents ?? eventsQuery.data ?? []).filter(
      (event) => event.attendees?.some((attendee) => attendee.id === userId) && !event.dontBlockAvailability
    );

    return Array.from({ length: dayCount + 1 }, (_, offset) =>
      getAvailableRangesForDay(
        availabilities,
        events,
        addDays(calcDate, offset),
        timezone,
        offset === 0 ? calcMs : undefined
      )
    )
      .flat()
      .sort((a, b) => a.start.getTime() - b.start.getTime())
      .filter(({ end }) => isFuture(end));
  }, [user?.weeklyAvailabilities, enabled, calcDate, providedEvents, eventsQuery.data, dayCount, userId, timezone]);
};
